import { useMemo, useCallback, useEffect, useRef } from 'react';
import { debounce, isEqual } from 'lodash';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import getListOrTableField from '@helpers/getListOrTableField';
import trackEvents from '@/helpers/trackEvents';

import formValuesToResponses from '../../helpers/formValuesToResponses';

type Props = {
  formData: {
    sections: Array<
      & Parameters<typeof formValuesToResponses>[3][number]
    >
  }
  formValues: Record<string, unknown>;
};

const HandleAutoSave: React.FC<Props> = ({ formData, formValues }) => {
  const {
    fieldsById,
    onSubmit,
  } = useQuestionnaireContext();
  const {
    formMethods,
    getResponseSource,
    getAiRequestId,
  } = useQuestionnaireFormContext();
  trackEvents.track('render - HandleAutoSave');

  const prevResponses = useRef<Record<string, unknown>>();
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const currentResponses = useMemo(
    () => formValuesToResponses(
      formValues,
      getResponseSource,
      getAiRequestId,
      formData.sections,
      (fieldId: string) => Boolean(getListOrTableField({ fieldId, fieldsById })),
    ),
    [JSON.stringify(formValues), formData.sections, fieldsById, getResponseSource, getAiRequestId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce...
  const debouncedSaveDraft = useCallback(
    debounce((data) => onSubmitRef.current(data), 2000),
    [],
  );

  useEffect(() => {
    if (!isEqual(currentResponses, prevResponses.current)) {
      prevResponses.current = currentResponses;
      const dirtyFieldKeys = Object.keys(formMethods.formState.dirtyFields);
      if (dirtyFieldKeys.length > 0) {
        const onlyChangedData = Object.keys(currentResponses).filter((fieldKey) => dirtyFieldKeys.includes(fieldKey)).reduce((acc, key) => ({
          ...acc,
          [key]: currentResponses[key],
        }), {});
        onSubmitRef.current(onlyChangedData);
        dirtyFieldKeys.forEach(key => formMethods.resetField(key, { defaultValue: formMethods.getValues()[key] }));
      }
    }
  }, [currentResponses, debouncedSaveDraft, formMethods]);

  return null;
};

export default HandleAutoSave;